import { handleUpdateStudyReferringPhysician, STUDY_STATUS } from '@/entities/study';
import {
  handleCreateReferringPhysician,
  referringPhysicianInfoFormatter,
  usePhysicianStore,
  type ReferringPhysician,
} from '@/entities/physician';

import {
  generateFullName,
  showMessageError,
  showMessageInfo,
  showMessageSuccess,
} from '@/shared/lib';
import { useSetState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { t } from 'ttag';
import { facilitySelection } from '@/entities/facility';
import type {
  EditReferringPhysicianProps,
  ReferringPhysicianOption,
} from '../editStudyDetails.types';
import { EDIT_DRAWER_KEY } from '../../../../config/editStudyDetails';

interface RefPhysicianState {
  isOpenConfirmModal: boolean;
  referringPhysician: string | null;
  selectedReferringPhysician: any;
  visibleCreateReferringPhysicianDrawer: boolean;
  isCreatingReferringPhysician: boolean;
  isInitiating: boolean;
  isSaving: boolean;
}

interface RefPhysicianStoreData {
  referringPhysician: string | undefined;
  selectedReferringPhysician: any;
}

/**
 * Drives the drawer that changes a study's referring physician, including creating a new one inline.
 * Re-seeds the selection and its storeData baseline from the study each time the drawer opens, reads
 * fetchPhysicians from the physician store, and wires the physician select, the no-changes save
 * guard, the save, the close, the create-physician submit and its nested drawer toggle.
 *
 * @param props - The study data, open flag, referringPhysicians options,
 * handleUpdateReferringPhysicians, onClose, drawerKey and the updateStudyData callback.
 * @returns An object carrying the drawer state and setter, the storeData baseline ref, the
 * aborted-study flag, and the change, save-guard, close, save, create and toggle handlers.
 */
export const useEditRefPhysicianDrawer = ({
  data,
  open,
  referringPhysicians,
  handleUpdateReferringPhysicians,
  onClose,
  drawerKey,
  updateStudyData,
}: Pick<
  EditReferringPhysicianProps,
  | 'data'
  | 'open'
  | 'referringPhysicians'
  | 'handleUpdateReferringPhysicians'
  | 'onClose'
  | 'drawerKey'
  | 'updateStudyData'
>) => {
  /**
   * Holds the fetchPhysicians action taken from the physician store. Selected out of usePhysicianStore so only that slice of the store is subscribed to.
   *
   * @returns The store action that reloads physicians for a facility.
   */
  const fetchPhysiciansRequestAction = usePhysicianStore((s) => s.fetchPhysicians);

  const [state, setState] = useSetState<RefPhysicianState>({
    isOpenConfirmModal: false,
    referringPhysician: null,
    selectedReferringPhysician: null,
    visibleCreateReferringPhysicianDrawer: false,
    isCreatingReferringPhysician: false,
    isInitiating: false,
    isSaving: false,
  });

  const storeData = React.useRef<RefPhysicianStoreData | null>(null);

  const isAbortedStudy = data?.status === STUDY_STATUS.ABORTED;

  /**
   * Records the referring physician picked in the study details edit drawer. Looks the chosen option up in the loaded referringPhysicians list and stores both the matched record and the option value in state.
   *
   * @param name - Name of the select field, unused.
   * @param option - Selected physician option whose value holds the physician id.
   * @returns Nothing.
   */
  const handleReferringPhysicianChange = (name: string, option: ReferringPhysicianOption) => {
    /**
     * Holds the referring physician record whose value matches the selected option. Found with lodash find over the referringPhysicians list.
     *
     * @returns The matching referring physician option, or undefined.
     */
    const selectedReferringPhysician = _.find(
      referringPhysicians,
      (referringPhysician) => referringPhysician.value === option.value,
    );
    setState({
      selectedReferringPhysician,
      referringPhysician: option.value,
    });
  };

  /**
   * Seeds the referring-physician drawer from the study's info.referringPhysician. Builds a select option carrying the generated full name, id, phone1, nightPhone, fax and email into the storeData ref as the pristine baseline, then merges a deep clone of it into state and clears isInitiating.
   *
   * @returns Promise resolving once the baseline and the drawer state are populated.
   */
  const initiateState = async () => {
    setState({ isInitiating: true });
    const { info } = data || {};
    const { referringPhysician } = info || {};
    storeData.current = {
      referringPhysician: referringPhysician?.id,
      selectedReferringPhysician: {
        label: generateFullName(referringPhysician),
        title: generateFullName(referringPhysician),
        value: referringPhysician?.id,
        name: generateFullName(referringPhysician),
        phone1: referringPhysician?.contact?.phone1,
        nightPhone: referringPhysician?.contact?.nightPhone,
        fax: referringPhysician?.contact?.fax,
        email: referringPhysician?.email,
      },
    };
    setState({
      ..._.cloneDeep(storeData.current),
      isInitiating: false,
    });
  };

  /**
   * Guards the referring-physician save behind a change check. Shows an information message when the selected physician still equals the baseline held in storeData, otherwise opens the confirmation modal.
   *
   * @returns Nothing.
   */
  const handleClickSave = () => {
    if (state.referringPhysician === storeData.current?.referringPhysician) {
      showMessageInfo({ content: t`No changes were made.` });
      return;
    }

    setState({ isOpenConfirmModal: true });
  };

  /**
   * Commits the chosen referring physician to the study. Sends studyId and the selected physician through handleUpdateStudyReferringPhysician, closes the confirm modal, shows a success message and pushes the returned physician to updateStudyData under the referring-physician drawer key, while failures are logged and reported and isSaving is always cleared.
   *
   * @returns Promise resolving once the update finished and the saving flag was cleared.
   */
  const onSave = async () => {
    setState({ isSaving: true });
    try {
      const input = {
        studyId: data?.id,
        referringPhysician: state.referringPhysician as string,
      };
      const result = await handleUpdateStudyReferringPhysician(input);
      setState({ isOpenConfirmModal: false });
      showMessageSuccess({ content: t`Referring physician updated successfully.` });
      const { referringPhysician } = result?.study?.info || {};
      updateStudyData(
        {
          referringPhysician: referringPhysician
            ? {
                id: referringPhysician.id,
                firstName: referringPhysician.firstName ?? '',
                lastName: referringPhysician.lastName ?? '',
                email: referringPhysician.email,
                npi: referringPhysician.npi ?? '',
                contact: {
                  fax: referringPhysician.contact?.fax ?? null,
                  nightPhone: referringPhysician.contact?.nightPhone ?? null,
                  phone1: referringPhysician.contact?.phone1 ?? null,
                },
              }
            : undefined,
        },
        EDIT_DRAWER_KEY.REFERRING_PHYSICIAN,
      );
    } catch (error) {
      console.error('Failed to update referring physician: ', error);
      showMessageError({ content: t`Failed to update referring physician.` });
    } finally {
      setState({ isSaving: false });
    }
  };

  /**
   * Closes the referring-physician edit drawer. Forwards the drawer key to the onClose prop.
   *
   * @returns Nothing.
   */
  const handleOnClose = () => {
    if (onClose && drawerKey) {
      onClose(drawerKey);
    }
  };

  /**
   * Creates a new referring physician for the study's facility from the nested drawer form. Formats the input with referringPhysicianInfoFormatter, calls handleCreateReferringPhysician with the facility id, adds the returned user to the local physician options, closes the create drawer and refetches physicians for the facility stored in local storage; errors are logged, shown and clear the creating flag.
   *
   * @param referringPhysicianInfo - Form values of the referring physician to create.
   * @returns Promise resolving once the physician was created or the failure was reported.
   */
  const handleClickCreateReferringPhysician = async (
    referringPhysicianInfo: ReferringPhysician,
  ) => {
    try {
      setState({ isCreatingReferringPhysician: true });
      const formattedReferringPhysicianInfo =
        referringPhysicianInfoFormatter(referringPhysicianInfo);
      const user = await handleCreateReferringPhysician(
        formattedReferringPhysicianInfo,
        data?.facility?.id,
      );
      if (handleUpdateReferringPhysicians) {
        handleUpdateReferringPhysicians(user);
      }
      setState({
        visibleCreateReferringPhysicianDrawer: false,
        isCreatingReferringPhysician: false,
      });

      const selectedFacilityLocalStorage = facilitySelection.selectedFacilityLocalStorage();
      fetchPhysiciansRequestAction(selectedFacilityLocalStorage.value);
    } catch (error) {
      console.error('Failed to create referring physician: ', error);
      showMessageError({ content: error instanceof Error ? error.message : String(error) });
      setState({ isCreatingReferringPhysician: false });
    }
  };

  /**
   * Shows or hides the nested drawer used to create a new referring physician. Flips visibleCreateReferringPhysicianDrawer in state.
   *
   * @returns Nothing.
   */
  const toggleDisplayCreateReferringPhysicianDrawer = () => {
    setState({
      visibleCreateReferringPhysicianDrawer: !state.visibleCreateReferringPhysicianDrawer,
    });
  };

  useEffect(() => {
    if (open) {
      initiateState();
    }
  }, [open]);

  return {
    state,
    setState,
    storeData,
    isAbortedStudy,
    handleReferringPhysicianChange,
    handleClickSave,
    handleOnClose,
    onSave,
    handleClickCreateReferringPhysician,
    toggleDisplayCreateReferringPhysicianDrawer,
  };
};
